import type { $ } from "../../dom";
import { Input, type InputOptions } from "./input";
import { isInputWithItemValue } from "./input-with-item-value";

export type MultiInputOptions<TValue> = Omit<
  InputOptions<TValue[]>,
  "resetValue"
> & {
  maxActiveCount?: number;
};

export abstract class MultiInput<TValue, TData = TValue> extends Input<
  NonNullable<TValue>[],
  NonNullable<TData>[]
> {
  public readonly maxActiveCount: number;

  public abstract inputs: Input<TValue, TData>[];

  private readonly lastActivatedInputs: Input<TValue, TData>[] = [];
  private _isHandlingChildToggle = false;

  constructor($root: $, options: MultiInputOptions<TValue>) {
    const { maxActiveCount = Infinity, ...superOptions } = options;

    super($root, {
      resetValue: [],
      ...superOptions,
    });

    this.maxActiveCount = Math.max(maxActiveCount, 0) || Infinity;
  }

  protected abstract readonly inputFactory: () => Input<TValue, TData>[];

  protected bindInputs(): void {
    this.inputs = this.inputFactory();

    for (const input of this.inputs) {
      this.registerChildInput(input);
    }

    this.lastActivatedInputs.length = 0;

    for (const input of this.inputs) {
      if (input.isActive) {
        this.addRecentlyActivatedChild(input);
      }
    }
  }

  public reinstantiateInputs(): void {
    this.destroyInputs();
    this.bindInputs();
  }

  protected registerChildInput(input: Input<TValue, TData>): void {
    input.onInput(() => {
      this.handleChildToggle(input);
    });

    input.forwardEventsTo(this);
  }

  private removeActiveChild(input: Input<TValue, TData>): void {
    const index = this.lastActivatedInputs.indexOf(input);

    if (index !== -1) {
      this.lastActivatedInputs.splice(index, 1);
    }
  }

  private addRecentlyActivatedChild(input: Input<TValue, TData>): void {
    this.removeActiveChild(input);

    this.lastActivatedInputs.push(input);
  }

  protected handleChildToggle(child: Input<TValue, TData>): void {
    if (this._isHandlingChildToggle) {
      return;
    }

    if (!Number.isFinite(this.maxActiveCount) || this.maxActiveCount === 0) {
      return;
    }

    if (child.isActive) {
      this.addRecentlyActivatedChild(child);

      this.sleepOnInput();
      this.sleepOnChange();

      try {
        while (this.lastActivatedInputs.length > this.maxActiveCount) {
          const oldest = this.lastActivatedInputs.shift();

          if (oldest && oldest !== child && oldest.isActive) {
            oldest.reset();
          }
        }
      } finally {
        this.wakeOnInput();
        this.wakeOnChange();
      }
    } else {
      this.removeActiveChild(child);
    }
  }

  static isMultiInput<T, D>(
    x: Input<T, D> | MultiInput<T, D>,
  ): x is MultiInput<T, D> {
    return x instanceof MultiInput;
  }

  get value(): NonNullable<TValue>[] {
    return this.inputs
      .filter((input) => input.value !== null && input.value !== undefined)
      .map((input) => input.value!);
  }

  set value(value: NonNullable<TValue>[]) {
    this.sleepOnInput();
    this.sleepOnChange();

    this.inputs.forEach((input, index) => {
      if (isInputWithItemValue(input)) {
        const itemValue = input.getItemValue() as NonNullable<TValue>;

        if (value.includes(itemValue)) {
          input.value = itemValue;
        } else {
          input.reset();
        }

        return;
      }

      if (value.length <= index) {
        input.reset();

        return;
      }

      input.value = value[index];
    });

    this.wakeOnInput();
    this.wakeOnChange();

    this.triggerInput();
    this.triggerChange();
  }

  get data(): NonNullable<TData>[] {
    return this.inputs
      .filter((input) => input.data !== null && input.data !== undefined)
      .map((input) => input.data!);
  }

  get isActive(): boolean {
    return this.inputs.some((input) => input.isActive);
  }

  private destroyInputs(): void {
    for (const input of this.inputs) {
      input.destroy();
    }

    this.lastActivatedInputs.length = 0;
  }

  destroy(): void {
    this.destroyInputs();

    super.destroy();
  }
}
